/**
 * The Help Desk Review list (#1947 Phase 3) — Admins only, in the Help Desk
 * panel. One place for everything the Help Desk prepared that needs an
 * Admin's OK: money, payroll and city-setup fixes. No bell and no phone ping;
 * the tab's number is the only signal. Corbin sees every one, whoever OK'd it.
 *
 * Approve runs the fix under the approving Admin's own sign-in, through the
 * page's own route (same as the person's own one-tap fixes).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { ActionPreviewBody, type ActionCard } from "@/components/help-desk-action-card";

export interface ListedAction extends ActionCard {
    askedBy: string | null;
    askedAt: string;
    decidedAt: string | null;
}

export interface ManualDraft {
    id: string;
    manual: string;
    heading: string;
    body: string;
    question: string;
    askedCount: number;
}

export interface WeekCount {
    weekStart: string;
    questions: number;
    answered: number;
    fixed: number;
    filed: number;
    share: number | null;
}

export const reviewKey = ["help-desk", "review"] as const;

export function useHelpDeskReview(enabled: boolean) {
    return useQuery<{
        waiting: ListedAction[];
        recent: ListedAction[];
        drafts?: ManualDraft[];
        weeks?: WeekCount[];
        goal?: number;
    }>({
        queryKey: reviewKey,
        queryFn: () => api.get(`/api/help-desk/review`),
        enabled,
        staleTime: 30_000,
        refetchInterval: enabled ? 60_000 : false,
        retry: false,
    });
}

function when(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function errText(e: unknown): string {
    if (e instanceof ApiError) return (e.body as { message?: string } | undefined)?.message ?? e.message;
    return e instanceof Error ? e.message : "That didn't work.";
}

function WaitingCard({ a, onDecided }: { a: ListedAction; onDecided: (text: string) => void }) {
    const qc = useQueryClient();
    const [declining, setDeclining] = useState(false);
    const [note, setNote] = useState("");
    const [msg, setMsg] = useState<string | null>(null);
    const decide = useMutation<{ card: ActionCard; message?: string }, unknown, "approve" | "decline">({
        mutationFn: (what) => api.post(`/api/help-desk/actions/${a.id}/${what}`, what === "decline" ? { note } : {}),
        onSuccess: (r) => {
            // A changed record keeps the card here with the new preview.
            if (r.message) {
                setMsg(r.message);
            } else if (r.card.status === "done") {
                onDecided(`✓ ${r.card.result?.text ?? "Done."} ${a.askedBy ?? "They"} will see ✓ in My questions.`);
            } else if (r.card.status === "declined") {
                onDecided(`Declined. ${a.askedBy ?? "They"} will see why in My questions.`);
            } else {
                setMsg(r.card.error ?? "That didn't work.");
            }
            void qc.invalidateQueries({ queryKey: reviewKey });
        },
        onError: (e) => setMsg(errText(e)),
    });
    return (
        <div className="hact high" data-testid="review-waiting">
            <div className="hact-h">
                ▲ {a.askedBy ?? "Someone"} asked · {when(a.askedAt)}
            </div>
            <div className="hact-b">
                <div className="hact-t">{a.preview.title}</div>
                <ActionPreviewBody card={a} />
                {msg && <div className="hact-note">{msg}</div>}
                {declining ? (
                    <>
                        <input
                            className="hrv-note"
                            aria-label="Why not?"
                            placeholder="Why not? (they'll see this)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                        <div className="hact-btns">
                            <button
                                type="button"
                                className="hact-do"
                                disabled={decide.isPending}
                                onClick={() => decide.mutate("decline")}
                            >
                                Decline
                            </button>
                            <button type="button" className="hact-cancel" onClick={() => setDeclining(false)}>
                                Back
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="hact-btns">
                        <button
                            type="button"
                            className="hact-do"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate("approve")}
                        >
                            OK, do it
                        </button>
                        <button
                            type="button"
                            className="hact-cancel"
                            disabled={decide.isPending}
                            onClick={() => setDeclining(true)}
                        >
                            Decline
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/** "This week: 22 questions · 15 answered · 4 fixed · 3 filed" with the 7-in-10 line. */
function WeekLine({ weeks, goal }: { weeks: WeekCount[]; goal: number }) {
    const w = weeks[0];
    if (!w || w.questions === 0) return null;
    const share = w.share ?? 0;
    const inTen = Math.round(share * 10);
    const pct = (n: number) => `${(n / w.questions) * 100}%`;
    return (
        <div className="hrv-count" data-testid="help-desk-weekly">
            <div>
                <b>This week:</b> {w.questions} questions · {w.answered} answered · {w.fixed} fixed · {w.filed} sent on
            </div>
            <div className="hrv-bar" role="img" aria-label={`${inTen} in 10 answered or fixed`}>
                <span style={{ width: pct(w.answered), background: "#3C6E93" }} />
                <span style={{ width: pct(w.fixed), background: "#4F7A52" }} />
                <span style={{ width: pct(w.filed), background: "#B23B36" }} />
            </div>
            <div>
                <b>{inTen} in 10 answered or fixed</b> · goal {Math.round(goal * 10)} in 10 {share >= goal ? "✓" : ""}
                {weeks[1] && weeks[1].share != null ? ` · last week ${Math.round(weeks[1].share * 10)} in 10` : ""}
            </div>
        </div>
    );
}

function DraftCard({ d, onDone }: { d: ManualDraft; onDone: (text: string) => void }) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [heading, setHeading] = useState(d.heading);
    const [body, setBody] = useState(d.body);
    const decide = useMutation<unknown, unknown, "approved" | "skipped">({
        mutationFn: (decision) =>
            api.post(`/api/help-desk/manual-drafts/${d.id}`, {
                decision,
                ...(editing ? { heading, body } : {}),
            }),
        onSuccess: (_r, decision) => {
            onDone(
                decision === "approved"
                    ? `✓ Added to the ${d.manual} manual. The Help Desk answers with it now.`
                    : "Skipped.",
            );
            void qc.invalidateQueries({ queryKey: reviewKey });
        },
    });
    return (
        <div className="hact" data-testid="review-draft" style={{ borderStyle: "dashed", borderColor: "#B98A46" }}>
            <div className="hact-h" style={{ background: "#EBDDC4", color: "#7a5a28" }}>
                Manual draft · asked {d.askedCount} {d.askedCount === 1 ? "time" : "times"}
            </div>
            <div className="hact-b">
                <div className="hrv-who">“{d.question}”</div>
                {editing ? (
                    <>
                        <input
                            className="hrv-note"
                            aria-label="Heading"
                            value={heading}
                            onChange={(e) => setHeading(e.target.value)}
                        />
                        <textarea
                            className="hrv-note"
                            aria-label="Section"
                            rows={6}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    </>
                ) : (
                    <>
                        <div className="hact-t">
                            {d.manual} · {d.heading}
                        </div>
                        <div className="hb gw" style={{ maxWidth: "100%", whiteSpace: "pre-wrap" }}>
                            {d.body.replace(/\*\*/g, "")}
                        </div>
                    </>
                )}
                <div className="hact-btns">
                    <button
                        type="button"
                        className="hact-do"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate("approved")}
                    >
                        Add to manual
                    </button>
                    <button type="button" className="hact-cancel" onClick={() => setEditing((v) => !v)}>
                        {editing ? "Done editing" : "Edit"}
                    </button>
                    <button
                        type="button"
                        className="hact-cancel"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate("skipped")}
                    >
                        Skip
                    </button>
                </div>
            </div>
        </div>
    );
}

const STATUS_WORDS: Record<string, string> = {
    done: "✓ Done",
    failed: "Didn't work",
    declined: "Declined",
    undone: "↺ Undone",
};

export function HelpDeskReview() {
    const q = useHelpDeskReview(true);
    const [flash, setFlash] = useState<string | null>(null);
    if (q.isLoading) return <div className="hb gw">Getting the Review list…</div>;
    if (q.isError || !q.data) return <div className="hb gw">I couldn&apos;t load the Review list just now.</div>;
    const waiting = q.data.waiting ?? [];
    const recent = q.data.recent ?? [];
    const drafts = q.data.drafts ?? [];
    const weeks = q.data.weeks ?? [];
    return (
        <div className="hrv" data-testid="help-desk-review">
            {flash && (
                <div className={flash.startsWith("✓") ? "hact-done" : "hact-undone"} role="status">
                    <span>{flash}</span>
                </div>
            )}
            <WeekLine weeks={weeks} goal={q.data.goal ?? 0.7} />
            <h4 className="hrv-h">Waiting for your OK · {waiting.length}</h4>
            {waiting.length === 0 ? (
                <div className="hb gw">
                    Nothing is waiting. Money, payroll and city fixes the Help Desk sets up show here.
                </div>
            ) : (
                waiting.map((a) => <WaitingCard key={a.id} a={a} onDecided={setFlash} />)
            )}
            {drafts.length > 0 && (
                <>
                    <h4 className="hrv-h">Manual drafts · {drafts.length}</h4>
                    {drafts.map((d) => (
                        <DraftCard key={d.id} d={d} onDone={setFlash} />
                    ))}
                </>
            )}
            {recent.length > 0 && (
                <>
                    <h4 className="hrv-h">The last 30 days</h4>
                    <div>
                        {recent.map((a) => (
                            <div key={a.id} className="hrv-row">
                                <span>
                                    <b>{a.preview.title}</b> · {a.askedBy ?? "someone"} asked
                                    {a.decidedBy
                                        ? ` · ${a.decidedBy} ${a.status === "declined" ? "said no" : "OK'd"}`
                                        : ""}
                                </span>
                                <span className="hrv-who">{STATUS_WORDS[a.status] ?? a.status}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
